import {
  TellescopeCreateRequestFromName,
  useAuth,
  useHealthieAppointmentTypesQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { useCallback, useMemo, useState } from "react";
import { createIdMap } from "@tellescope/utils";

export interface UseHealthieAppointmentTypeImportProps {
  /** Called after a successful import (or when there is nothing to import). */
  onImportSuccess: () => void | Promise<void>;
}

/**
 * Loads Healthie appointment types, tracks the user's selection, and imports the
 * selected types as `calendar_event_templates`. UI-agnostic so it can back both
 * the onboarding flow step and an ad-hoc import dialog.
 */
export const useHealthieAppointmentTypeImport = ({ onImportSuccess }: UseHealthieAppointmentTypeImportProps) => {
  const { isAdmin } = useAuth();
  // null = all appointment types selected (the default)
  const [selectedAppointmentTypeIds, setSelectedAppointmentTypeIds] = useState<string[] | null>(null);

  const appointmentTypesQuery = useHealthieAppointmentTypesQuery();

  // Appointment types already imported as templates (matched on healthieTemplateId) are excluded below.
  const importedTemplatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {
      limit: 100,
      mdbFilter: { healthieTemplateId: { $nin: [null, ""] } },
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });
  const importedHealthieTemplateIds = useMemo(
    () =>
      (importedTemplatesQuery.data ?? []).reduce<Set<string>>((acc, template) => {
        if (template.healthieTemplateId) acc.add(template.healthieTemplateId);
        return acc;
      }, new Set()),
    [importedTemplatesQuery.data]
  );

  const allAppointmentTypes = useMemo(() => appointmentTypesQuery.data?.data ?? [], [appointmentTypesQuery.data?.data]);
  const appointmentTypes = useMemo(
    () => allAppointmentTypes.filter(({ id }) => !importedHealthieTemplateIds.has(id)),
    [allAppointmentTypes, importedHealthieTemplateIds]
  );
  const isLoading = appointmentTypesQuery.isPending || importedTemplatesQuery.isPending;
  const isError = appointmentTypesQuery.isError;
  // Empty because every Healthie appointment type is already imported, rather than because Healthie returned none.
  const allAlreadyImported = !isLoading && !isError && allAppointmentTypes.length > 0 && appointmentTypes.length === 0;

  const createSomeCalendarEventTemplatesMutation = useTellescopeMutation({
    model: "calendar_event_templates",
    method: "createSome",
  });
  const isImporting = createSomeCalendarEventTemplatesMutation.isPending;

  const resolvedSelectedAppointmentIds = selectedAppointmentTypeIds ?? appointmentTypes.map(({ id }) => id);
  const allSelected = appointmentTypes.length > 0 && resolvedSelectedAppointmentIds.length === appointmentTypes.length;
  const hasSelection = resolvedSelectedAppointmentIds.length > 0;

  const toggleAppointmentType = useCallback(
    (id: string) => {
      setSelectedAppointmentTypeIds((current) => {
        const resolved = current ?? appointmentTypes.map((appointmentType) => appointmentType.id);
        return resolved.includes(id) ? resolved.filter((selectedId) => selectedId !== id) : [...resolved, id];
      });
    },
    [appointmentTypes]
  );

  const setAllSelected = useCallback((checked: boolean) => {
    setSelectedAppointmentTypeIds(checked ? null : []);
  }, []);

  const importSelected = useCallback(async () => {
    const appointmentMap = createIdMap(appointmentTypes);
    const items = resolvedSelectedAppointmentIds.reduce<TellescopeCreateRequestFromName<"calendar_event_templates">[]>(
      (acc, id) => {
        const match = appointmentMap[id];
        if (!match) return acc;

        acc.push({
          title: match.name,
          durationInMinutes: match.length ?? 60,
          healthieTemplateId: id,
        });
        return acc;
      },
      []
    );
    if (!items.length) {
      await onImportSuccess();
      return;
    }
    await createSomeCalendarEventTemplatesMutation.mutateAsync(
      { items, _options: { _overrideUnique: true } },
      { onSuccess: onImportSuccess }
    );
  }, [appointmentTypes, createSomeCalendarEventTemplatesMutation, onImportSuccess, resolvedSelectedAppointmentIds]);

  const canImport = isAdmin && !isLoading && !isError && (appointmentTypes.length === 0 || hasSelection);

  return {
    appointmentTypes,
    isLoading,
    isError,
    allAlreadyImported,
    isImporting,
    resolvedSelectedAppointmentIds,
    allSelected,
    hasSelection,
    isAdmin,
    canImport,
    toggleAppointmentType,
    setAllSelected,
    importSelected,
  };
};
